import { ImageIcon, Layers, LayoutGrid, Plus } from "lucide-react";
import { cn } from "@/modern/utils";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

interface WorkFileCardListProps {
  cards: MarketingWorkFileCard[];
  selectedCardId: number | null;
  onSelectCard: (id: number | null) => void;
  onAddCard?: () => void;
  isAddingCard?: boolean;
}

export const WorkFileCardList = ({
  cards,
  selectedCardId,
  onSelectCard,
  onAddCard,
  isAddingCard: _isAddingCard = false,
}: WorkFileCardListProps) => {
  return (
    <aside className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-background">
      {/* Sticky sub-header */}
      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-border bg-card px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          Sections
          <span className="text-muted-foreground">· {cards.length}</span>
        </span>
        <button
          type="button"
          aria-label="Add card"
          onClick={onAddCard}
          className="inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-3 w-3" />
          New
        </button>
      </div>

      {/* Scrollable list */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {cards.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-dashed border-border bg-muted/40 text-muted-foreground">
              <LayoutGrid className="h-6 w-6" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground">No cards yet</p>
              <p className="max-w-[200px] text-xs text-muted-foreground">
                Add your first card to start building this work file.
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs"
              onClick={onAddCard}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Card
            </Button>
          </div>
        ) : (
          <ul className="space-y-2.5 p-3">
            {cards.map((card) => (
              <WorkFileCardListItem
                key={card.id}
                card={card}
                selected={card.id === selectedCardId}
                onSelect={() => onSelectCard(card.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
};

interface WorkFileCardListItemProps {
  card: MarketingWorkFileCard;
  selected: boolean;
  onSelect: () => void;
}

const WorkFileCardListItem = ({ card, selected, onSelect }: WorkFileCardListItemProps) => {
  const { project } = useSwivuBryntumContext();

  const firstThumbSrc = (() => {
    const assetStore = project?.getCrudStore(STORE_KEYS.marketingAssetStore);
    if (!assetStore || !card.assets?.length) return null;
    const asset = assetStore.getById(card.assets[0]) as unknown as MarketingAsset | undefined;
    const boardFile = asset?.fileRecord as unknown as BoardFile | undefined;
    return (boardFile as any)?.url ?? (boardFile as any)?.thumbnailUrl ?? null;
  })();

  const assetCount = card.assets?.length ?? 0;
  const assetLabel = `${assetCount} asset${assetCount === 1 ? "" : "s"}`;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
          "group relative flex w-full items-start gap-3 overflow-hidden rounded-lg border-y border-r p-3 text-left transition-all duration-150",
          "border-l-[3px] outline-none focus-visible:ring-2 focus-visible:ring-ring",
          selected
            ? "border-l-primary border-y-primary/20 border-r-primary/20 bg-primary/10"
            : "border-l-border/70 border-y-border/70 border-r-border/70 bg-card hover:bg-primary/5 hover:border-l-primary/40",
        )}
      >

        {firstThumbSrc ? (
          <img
            src={firstThumbSrc}
            alt=""
            className="h-10 w-10 shrink-0 rounded-md border border-border object-cover shadow-sm"
          />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-dashed border-border bg-muted/40 text-muted-foreground/50">
            <ImageIcon className="h-4 w-4" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "line-clamp-2 text-sm font-medium leading-snug",
              card.heading?.trim() ? "text-foreground" : "text-muted-foreground/40",
            )}
          >
            {card.heading?.trim() || "Heading"}
          </div>
          {card.heading?.trim() ? (
            card.subHeading?.trim() && (
              <p className="mt-0.5 truncate text-xs leading-snug text-muted-foreground">
                {card.subHeading}
              </p>
            )
          ) : (
            <p className="mt-0.5 truncate text-xs leading-snug text-muted-foreground/30">
              Sub Heading
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
            <ImageIcon className="h-3 w-3" />
            <span>{assetLabel}</span>
          </div>
        </div>
      </button>
    </li>
  );
};

export default WorkFileCardList;
